import CouponRefundPageView from 'src/pages/CouponRefundPage/view'
import useCouponRefundForm from 'src/hooks/react-hook-form/useCouponRefundForm.hook'
import { FormProvider, SubmitHandler } from 'react-hook-form'
import { CouponRefundFormItemsType, FormObjectType } from 'src/types/react-hook-form'
import {
  passCertCheckQueryOption,
  refundBankListQueryOption,
  useCustomQuery,
} from 'src/hooks/react-query/useCustomQuery.hook'
import { QueryClient } from '@tanstack/react-query'
import {
  refundSubmitQueryOption,
  refundTelecomCertFileQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import {
  BankListSpace,
  CouponRefundSpace,
  PassCertResponseSpace,
  PassReqType,
  RefundTelecomCertSubmitFileSpace,
  ResponseDataType,
} from 'src/types/api'
import React, { useEffect, useState } from 'react'
import { ERRCODE_SUCCESS } from 'src/service/constant/errcodes'
import { useModal } from 'src/provider/ModalContextProvider'
import useParamsType from 'src/hooks/useParamsType.hook'
import usePageNav from 'src/hooks/usePageNav.hook'
import RenderIf from 'src/components/common/RenderIf'
import { Loading } from 'src/components/common/Loading'
import { useChannel } from 'src/provider/ChannelContextProvider'
import usePageVisibility from 'src/hooks/usePageVisibility.hook'
import usePassCert from 'src/hooks/usePassCert.hook'
import { v4 } from 'uuid'
import { Bridge } from 'src/utils/bridge'
import { DateUtils } from 'src/utils/date'
import RefundInfoConfirmModal from 'src/components/common/Modal/children/RefundInfoConfirmModal'

export interface CouponRefundPageProps {
  registerAttribute: Record<keyof CouponRefundFormItemsType, Partial<FormObjectType>>
  refundSubmitRequestHandler: SubmitHandler<CouponRefundFormItemsType>
  passCertRequest: (type: PassReqType) => void
  userName: string
}

export const couponRefundPageLoader = (queryClient: QueryClient) => async () => {
  const dataQueryValues = [
    await queryClient.ensureQueryData(refundBankListQueryOption()),
    await queryClient.ensureQueryData(passCertCheckQueryOption()),
  ]
  return Promise.allSettled(dataQueryValues)
}

const CouponRefundPage = () => {
  const [uuid, setUuid] = useState(v4())
  const [userName, setUserName] = useState('')
  const { couponNumber, orderId, orderDT, idx } = useParamsType()
  const { openModal } = useModal()
  const { aos, web } = useChannel()
  const { couponPurchaseRefundCompletePageNav } = usePageNav()
  const { isValidInDays } = DateUtils
  const { methods, registerAttribute } = useCouponRefundForm(userName)
  const { reqNum, passCertRequest, passCertResponse, isPassCert, isPassCertCheckLoading } =
    usePassCert(uuid)
  const [isUserNameLoading, setIsUserNameLoading] = useState(!web && isPassCert)

  const { data: bankListQueryData, isLoading: isBankListLoading } =
    useCustomQuery<BankListSpace.Response>(refundBankListQueryOption())

  const { mutateAsync: refundMutate, isPending: refundPending } = useCustomMutation<
    CouponRefundSpace.Request,
    CouponRefundSpace.Response
  >(refundSubmitQueryOption())

  const { mutateAsync: refundCertFileMutate, isPending: refundCertFilePending } = useCustomMutation<
    RefundTelecomCertSubmitFileSpace.Request,
    RefundTelecomCertSubmitFileSpace.Response
  >(refundTelecomCertFileQueryOption())

  const refundSubmit = (data: CouponRefundFormItemsType & { TelecomCertFile: string }) => {
    const { name, TelecomCertFile, bankNum, accountNumber } = data
    const submitData = {
      Idx: Number(idx),
      Name: name,
      PassName: userName,
      TelecomCertFile: TelecomCertFile,
      BankNum: Number(bankNum),
      AccountNumber: accountNumber,
    }
    refundMutate(submitData).then(({ Header, Body }) => {
      const { ErrCode } = Header
      if (ErrCode === ERRCODE_SUCCESS) {
        couponPurchaseRefundCompletePageNav(Number(idx), couponNumber, orderId)
      }
    })
  }

  const refundCertFileSubmit = (data: CouponRefundFormItemsType) => {
    if (!!userName) {
      refundSubmit({ ...data, TelecomCertFile: '' })
    } else {
      const { telecomCertFile } = data
      refundCertFileMutate({ TelecomCertFile: telecomCertFile.file }).then(({ Header, Body }) => {
        if (Header.ErrCode === ERRCODE_SUCCESS) {
          const { TelecomCertFile } = Body
          refundSubmit({ ...data, TelecomCertFile })
        }
      })
    }
  }

  const refundSubmitRequestHandler: SubmitHandler<CouponRefundFormItemsType> = (data) => {
    const { name, bankNum, accountNumber } = data

    const selectedBankName = bankListQueryData?.Body.BankInfo.find(
      ({ BankNum }: BankListSpace.BankList) => BankNum === Number(bankNum),
    )?.BankName as string

    const InformationEnteredList = [
      { text: '은행명 : ', value: selectedBankName },
      { text: '계좌번호 :', value: accountNumber },
      { text: '예금주 이름 :', value: name },
      {
        text: '환불금액 :',
        value: `${isValidInDays(orderDT, 365) ? '100' : '90'}% 환불 예정`,
      },
    ]

    openModal({
      contents: {
        t: '입력한 정보를 확인해주세요',
        children: <RefundInfoConfirmModal InformationEnteredList={InformationEnteredList} />,
      },
      button: {
        cancel: { t: '수정하기' },
        submit: { t: '확인', f: () => refundCertFileSubmit(data) },
      },
    })
  }

  useEffect(() => {
    if (isPassCert) {
      Bridge.getUserName.req(() => setIsUserNameLoading(false))
      Bridge.getUserName.res((result) => {
        const name = result !== 'name_error' ? result : ''
        setFormName(name)
        setIsUserNameLoading(false)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPassCert])

  useEffect(() => {
    if (aos) handlePassCertResponse()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqNum])

  usePageVisibility((isForeground: boolean) => {
    if (isForeground && !aos && !userName) {
      handlePassCertResponse()
    }
  })

  const handlePassCertResponse = () => {
    const successFn = (res: ResponseDataType<PassCertResponseSpace.Response>) => {
      const name = res.Body.Name
      setFormName(name)
    }
    const failFn = () => setUuid(v4())
    passCertResponse(0, successFn, failFn)
  }

  const setFormName = (name: string) => {
    if (!!name) {
      setUserName(name)
      methods.clearErrors('name')
      methods.setValue('name', name)
      methods.setValue('telecomCertFile', { file: '/', name: '/' })
    }
  }

  const props: CouponRefundPageProps = {
    registerAttribute,
    refundSubmitRequestHandler,
    passCertRequest,
    userName,
  }

  return (
    <FormProvider {...methods}>
      <RenderIf
        when={
          isBankListLoading ||
          isUserNameLoading ||
          isPassCertCheckLoading ||
          refundPending ||
          refundCertFilePending
        }
      >
        <Loading.Default />
      </RenderIf>
      <CouponRefundPageView {...props} />
    </FormProvider>
  )
}
export default CouponRefundPage
